import React, {
  useImperativeHandle,
  forwardRef,
  useContext,
  useState,
  useEffect,
  useRef
} from "react";
import { useTranslation } from "react-i18next";
import { SearchContext } from "./Search";
import { getFieldValues, getFilters, getQueryLayers } from "./SearchUtils";
import Select from "react-select";
import { removeMapGraphics, showAlert } from "../../../../utils/Helpers";
import { capitalizeFirstLetter } from "../../../../utils/HelperFunctions";
import CustomDropdownIndicator from "../../../../components/common/CustomDropdownIndicator";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";

const DataSearch = forwardRef((props, ref) => {
  const { data } = props;
  const { t, i18n } = useTranslation();
  const {
    mapview,
    showLoading,
    setEnableActions,
    setSelectedResultLayerOption,
  } = useContext(SearchContext);
  const [service, setService] = useState("");
  const [queryLayers, setQueryLayers] = useState(null);
  const [queryLayer, setQueryLayer] = useState("");
  const [queryLayerUrl, setQueryLayerUrl] = useState("");
  const [layerFilter, setLayerFilter] = useState(null);
  const [fieldValues, setFieldValues] = useState([]);
  const selectedServiceRef = useRef({ id: "", text: "" });
  const selectedLayerRef = useRef({ id: "", text: "" });
  const [fieldValuesState, setFieldValuesState] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [errors, setErrors] = useState({});
  let clipledGraphics = useRef(new GraphicsLayer({
    id: "clipedGraphicsLayer",
    title: "Clipped Graphics Layer",
  }));

  useEffect(() => {
    if (!mapview || !queryLayer) return;

    const layerId = selectedLayerRef.current.id;

    const fetchFilters = async () => {
      /**get filters from api */
      showLoading(true);
      mapview.map.add(clipledGraphics.current);
      clipledGraphics.current.removeAll();
      const result = await getFilters(layerId);
      if (result.error) {
        showAlert(result.error, "error");
        showLoading(false);
      } else {
        const filterCriterias = result.data;
        if (filterCriterias.length === 0) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            queryFilter: t("queryFiltersNotFound"),
          }));
          setShowFilters(false);
          showLoading(false);
          return;
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, queryFilter: "" }));
        }
        const layerUrl = await getLayerFullUrl();
        if (!layerUrl) {
          showLoading(false);
          return;
        }
        /**get field values for dropdowns */
        const dropdownFieldNames = filterCriterias
          .filter((filterCriteria) => filterCriteria.isUniqueEnable)
          .map((filterCriteria) => filterCriteria.mapFieldName);
        if (dropdownFieldNames.length > 0) {
          await fetchFieldValues(dropdownFieldNames, layerUrl);
        }
        setLayerFilter(filterCriterias);
        showLoading(false);
      }
    };
    fetchFilters();
  }, [mapview, queryLayer]);

  const getLayerFullUrl = async () => {
    const mapserviceId = selectedServiceRef.current.id;
    const layerId = selectedLayerRef.current.id;
    const mapservice = data.find(
      (dataItem) => dataItem.id === parseInt(mapserviceId)
    )?.mapServiceURL;
    const layerIndex = queryLayers.find(
      (layerItem) => layerItem.id === parseInt(layerId)
    )?.layerIndex;
    if (!mapservice || layerIndex === undefined) {
      return null;
    }
    const layerUrl = `${mapservice}/${layerIndex}`;
    setQueryLayerUrl(layerUrl);
    return layerUrl;
  };

  const fetchFieldValues = async (fieldNamesArray, layerUrl) => {
    showLoading(true);
    const fieldValuesResult = await getFieldValues(layerUrl, fieldNamesArray);
    if (fieldValuesResult.error) {
      showAlert(fieldValuesResult.error, "error");
      showLoading(false);
    } else {
      if (fieldValuesResult.warning) {
        showAlert(t(fieldValuesResult.warning), "warning");
      }
      const fvaluesMap = fieldNamesArray.reduce((acc, field) => {
        const valuesArray = fieldValuesResult[field]?.values || [];
        const formattedValues = valuesArray.map((value) => ({
          value: value,
          label: value,
        }));
        acc[field] = formattedValues;
        return acc;
      }, {});
      setFieldValues(fvaluesMap);
      showLoading(false);
    }
  };

  useEffect(() => {
    if (!layerFilter) return;

    async function setFieldValuesToState() {
      const acc = {};

      for (const filter of layerFilter) {
        switch (filter.controlsNo) {
          case 0:
            /**opr (null, not null) */
            acc[filter.mapFieldName] = {
              operator: filter.operatorSymbol,
            };
            break;
          case 2:
            /**opr (between, not between) */
            acc[filter.mapFieldName] = {
              startDate: "",
              endDate: "",
              operator: filter.operatorSymbol,
            };
            break;
          case 1:
          case null:
            if (filter.isUniqueEnable) {

              acc[filter.mapFieldName] = {
                value: "",
                open: false,
                operator: filter.operatorSymbol,
                filteredData: fieldValues[filter.mapFieldName],
              };
            } else {
              acc[filter.mapFieldName] = {
                value: "",
                operator: filter.operatorSymbol,
              };
            }
            break;
          default:
            break;
        }
      }

      setFieldValuesState(acc);
    }
    setFieldValuesToState();
    setShowFilters(true);
    showLoading(false);
  }, [layerFilter, fieldValues]);

  useEffect(() => {
    if (!service || service === "" || !queryLayer || queryLayer === "") {
      setEnableActions(false);
    } else {
      setEnableActions(true);
    }


  }, [service, queryLayer, fieldValuesState]);

  const handleDateChange = (field, event, date) => {
    const newValue = event.value || event.target.value;
    setFieldValuesState((prevState) => ({
      ...prevState,
      [field]: { ...prevState[field], [date]: newValue },
    }));
  };

  const handleOperatorChange = (field, event) => {
    const { value } = event.target;
    /**operator input */
    setFieldValuesState((prevState) => ({
      ...prevState,
      [field]: { ...prevState[field], operator: value },
    }));
  };

  const handleInputChange = (field, event) => {
    const { value } = event.target;
    /**value input */
    setFieldValuesState((prevState) => ({
      ...prevState,
      [field]: { ...prevState[field], value: value },
    }));
  };

  // const handleChange = (field, selectedOption) => {
  //   const newValue = selectedOption ? selectedOption.value : null;
  //   if (!newValue) return;
  //   const filter = parseInt(newValue) ? newValue : newValue.toLowerCase();
  //   const filteredData = fieldValues[field];
  //   setFieldValuesState((prevState) => ({
  //     ...prevState,
  //     [field]: {
  //       ...prevState[field],
  //       value: newValue,
  //       filteredData,
  //       open: true,
  //     },
  //   }));

  // };
  const handleChange = (field, selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setFieldValuesState(prev => ({
        ...prev,
        [field]: { ...prev[field], value: [], open: false }
      }));
      return;
    }

    const values = selectedOptions.map(opt => opt.value);

    setFieldValuesState(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: values,
        open: true,
        filteredData: fieldValues[field]
      }
    }));
  };

  const handleMapServiceChange = async (evt, label) => {
    selectedServiceRef.current = {
      id: evt,
      text: label,
    };
    const selection = evt;
    setService(
      selection
    ); /**store id to state variable as ref does not trigger a re-render */
    if (!selection) {
      return;
    }
    let result = await getQueryLayers(selection);
    if (result.error) {
      showAlert(result.error, "error");
    } else {
      const queryLayers = result.data;
      if (queryLayers.length === 0) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          queryLayer: t("queryNotFound"),
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, queryLayer: "" }));
      }
      setQueryLayer("");
      setShowFilters(false);
      setQueryLayers(queryLayers);
    }
  };

  const handleLayerChange = (evt, label) => {
    selectedLayerRef.current = {
      id: evt,
      text: label,
    };
    const selection = evt;
    setFieldValuesState({});
    setQueryLayer(selection);
    setSelectedResultLayerOption(selectedLayerRef.current.text);
    if (selectedLayerRef.current.id && selectedLayerRef.current.id !== "") {
      showLoading(true);
    } else {
      setShowFilters(false);
    }
  };

  const reset = () => {
    setService("");
    setQueryLayer("");
    setQueryLayers(null);
    setShowFilters(false);
    setFieldValuesState({});
    const clippedGraphics = mapview.map.findLayerById("clipedGraphicsLayer");
    if (clippedGraphics) {
      clippedGraphics.removeAll();
      mapview.map.remove(clippedGraphics);
    }
    mapview.map.layers.forEach((layer) => {
      if (layer.id === 'clipedGraphicsLayer' || layer.id === 'searchDrawGLayer') {
        layer.visible = false; // Keep the clipped graphics layer visible
      }
      else {
        // Hide all other layers
        layer.visible = true;
      }
    });
    removeMapGraphics(mapview);
  };


  useImperativeHandle(ref, () => ({
    reset,
    getQueryOptions,
    validateInputs,
    queryLayerUrl,
    selectedLayerRef,
  }));

  const validateInputs = () => {
    let validDates = true; let validInputs = true;
    let operatorValid = true;
    if (layerFilter) {
      layerFilter.map((fieldObj) => {
        const fieldName = fieldObj.mapFieldName;
        const fieldOperator = fieldObj.operatorSymbol;
        const fieldOperatorId = fieldObj.operatorId;
        if (fieldValuesState[fieldName]) {
          const fieldValue = fieldValuesState[fieldName].value;
          if (Array.isArray(fieldValue) && fieldValue.length === 0) {
            return;  
          }
          const fieldOperatorValue = fieldValuesState[fieldName].operator;
          if (!fieldValue || fieldValue === "") {
            return;
          }
          if ((fieldOperatorId > 6 && fieldOperatorId < 11)) {
            if (fieldOperatorId === 7 || fieldOperatorId === 8) {
              validDates = validateDates(fieldValuesState[fieldName])
            }
            if (fieldObj.controlsNo === null || fieldObj.controlsNo >= 1) {
              validInputs = validateFieldInput(fieldObj, fieldValue);
            }
          }
          if (fieldOperatorId === 15 && (fieldOperatorValue === null || fieldOperatorValue === "" || fieldOperatorValue === "All")) {
            operatorValid = false;
          }
        }
      });
    }

    return validInputs && validDates && operatorValid;
  }
  const getQueryWhere = () => {
    let where = "";
  
    if (layerFilter) {
      layerFilter.map((fieldObj) => {
        const fieldName = fieldObj.mapFieldName;
        const fieldOperator = fieldObj.operatorSymbol;
        const fieldOperatorId = fieldObj.operatorId;
  
        if (!fieldValuesState[fieldName]) return;
  
        const rawValue = fieldValuesState[fieldName].value;
        const operatorValue = fieldValuesState[fieldName].operator;
  
        // Skip empty single-value fields
        if (!(fieldOperatorId > 6 && fieldOperatorId < 11) &&
            (!rawValue || rawValue === "")) {
          return;
        }
  
        // Escape helper
        const escape = (v) => String(v).replace(/'/g, "''");
  
        // Normalize react-select values
        let value = null;
  
        if (Array.isArray(rawValue)) {
          if (rawValue.length === 0) return;
          value = rawValue.map(x => x?.value ?? x);
        } else if (typeof rawValue === "object" && rawValue !== null) {
          value = rawValue.value;
        } else {
          value = rawValue;
        }
  
        let condition = "";
  
        // Handle dates
        let startDate, endDate;
        if (fieldOperatorId === 7 || fieldOperatorId === 8) {
          if (validateDates(fieldValuesState[fieldName])) {
            startDate = fieldValuesState[fieldName].startDate;
            endDate = fieldValuesState[fieldName].endDate;
          }
        }
  
        // Handle operators
        switch (fieldOperatorId) {
          case 1: // equal
          case 2: // not equal
            if (Array.isArray(value)) {
              const escapedValues = value
                .map(v => `'${escape(String(v).toLowerCase())}'`)
                .join(",");
  
              condition = `LOWER(${fieldName}) IN (${escapedValues})`;
            } else {
              if (fieldObj.mapFieldType === "esriFieldTypeString") {
                condition = `LOWER(${fieldName}) ${operatorValue} LOWER('${escape(value)}')`;
              } else {
                condition = `${fieldName} ${operatorValue} ${value}`;
              }
            }
            break;
  
          case 7: // between
            if (startDate) {
              condition = `(${fieldName} >= '${startDate}') AND (${fieldName} <= '${endDate}')`;
            }
            break;
  
          case 8: // not between
            if (endDate) {
              condition = `NOT(${fieldName} >= '${startDate}' AND ${fieldName} <= '${endDate}')`;
            }
            break;
  
          case 9:
          case 10:
            condition = `${fieldName} ${fieldOperator}`;
            break;
  
          case 11: // starts with
            if (Array.isArray(value)) {
              const parts = value.map(v =>
                `LOWER(${fieldName}) LIKE LOWER('${escape(v)}%')`
              );
              condition = "(" + parts.join(" OR ") + ")";
            } else {
              condition = `LOWER(${fieldName}) LIKE LOWER('${escape(value)}%')`;
            }
            break;
  
          case 12: // ends with
            if (Array.isArray(value)) {
              const parts = value.map(v =>
                `LOWER(${fieldName}) LIKE LOWER('%${escape(v)}')`
              );
              condition = "(" + parts.join(" OR ") + ")";
            } else {
              condition = `LOWER(${fieldName}) LIKE LOWER('%${escape(value)}')`;
            }
            break;
  
          case 13: // contains
            if (Array.isArray(value)) {
              const parts = value.map(v =>
                fieldName.toLowerCase().includes("arabic")
                  ? `${fieldName} LIKE N'%${escape(v)}%'`
                  : `LOWER(${fieldName}) LIKE LOWER('%${escape(v)}%')`
              );
              condition = "(" + parts.join(" OR ") + ")";
            } else {
              condition = fieldName.toLowerCase().includes("arabic")
                ? `${fieldName} LIKE N'%${escape(value)}%'`
                : `LOWER(${fieldName}) LIKE LOWER('%${escape(value)}%')`;
            }
            break;
  
          case 14: // NOT contains
            if (Array.isArray(value)) {
              const parts = value.map(v =>
                `LOWER(TRIM(${fieldName})) NOT LIKE LOWER('%${escape(v)}%')`
              );
              condition = "(" + parts.join(" AND ") + ")";
            } else {
              condition = `LOWER(TRIM(${fieldName})) NOT LIKE LOWER('%${escape(value)}%')`;
            }
            break;
  
          default:
            condition = `${fieldName} ${operatorValue} ${value}`;
            break;
        }
  
        if (!condition || condition === "()") return;
  
        // Append to WHERE clause
        if (where) where += " AND " + condition;
        else where = condition;
  
      });
    }
  
    return where || "1=1";
  };
  
  // const getQueryWhere = () => {
  //   let where = "";

  //   if (layerFilter) {
  //     layerFilter.map((fieldObj) => {
  //       const fieldName = fieldObj.mapFieldName;
  //       const fieldOperator = fieldObj.operatorSymbol;
  //       const fieldOperatorId = fieldObj.operatorId;

  //       if (fieldValuesState[fieldName]) {
  //         const fieldValue = fieldValuesState[fieldName].value;
  //         const fieldOperatorValue = fieldValuesState[fieldName].operator;

  //         if (
  //           (fieldOperatorId > 6 && fieldOperatorId < 11) ||
  //           (fieldValue && fieldValue !== "")
  //         ) {
  //           let condition = "";
  //           //For single-select
  //           // const stringFieldValue = fieldValue?.replace(/'/g, "''"); // escape single quotes
  //           //For Multi-select
  //           let stringFieldValue = "";

  //           if (Array.isArray(fieldValue)) {
  //             stringFieldValue = fieldValue.map(v =>
  //               String(v).replace(/'/g, "''")
  //             );
  //           } else {
  //             stringFieldValue = String(fieldValue).replace(/'/g, "''");
  //           }

  //           // let startDate: "" | undefined;
  //           // let endDate: string | undefined;

  //           if (fieldOperatorId === 7 || fieldOperatorId === 8) {
  //             if (validateDates(fieldValuesState[fieldName])) {
  //               var startDate = fieldValuesState[fieldName].startDate;
  //               var endDate = fieldValuesState[fieldName].endDate;
  //             }
  //           }

  //           switch (fieldOperatorId) {
  //             case 1: // equal
  //             case 2: // not equal
  //               // if (fieldObj.mapFieldType === "esriFieldTypeString") {
  //               //   condition += `LOWER(${fieldName}) ${fieldOperatorValue} LOWER('${stringFieldValue}')`;
  //               // } else {
  //               //   condition += `${fieldName} ${fieldOperatorValue} ${fieldValue}`;
  //               // }
  //               if (Array.isArray(fieldValue)) {
  //                 const escapedValues = fieldValue
  //                   .map(v => `'${String(v).replace(/'/g, "''")}'`)
  //                   .join(",");

  //                 condition += `LOWER(${fieldName}) IN (${escapedValues.toLowerCase()})`;
  //               }
  //               else {
  //                 // SINGLE VALUE (existing logic)
  //                 if (fieldObj.mapFieldType === "esriFieldTypeString") {
  //                   condition += `LOWER(${fieldName}) ${fieldOperatorValue} LOWER('${stringFieldValue}')`;
  //                 } else {
  //                   condition += `${fieldName} ${fieldOperatorValue} ${fieldValue}`;
  //                 }
  //               }

  //               break;

  //             case 7: // between
  //               if (startDate) {
  //                 //condition += `(${fieldName} >= '${startDate}') AND (${fieldName} <= '${endDate}')`;
  //                 condition += `(${fieldName} >= DATE '${startDate}') AND (${fieldName} <= DATE '${endDate}')`;
  //               }
  //               break;

  //             case 8: // not between
  //               if (endDate) {
  //                 // condition += `NOT(${fieldName} >= '${startDate}') AND (${fieldName} <= '${endDate}')`;
  //                 condition += `NOT(${fieldName} >= DATE '${startDate}' AND ${fieldName} <= DATE '${endDate}')`;
  //               }
  //               break;

  //             case 9: // is null
  //             case 10: // is not null
  //               condition += `${fieldName} ${fieldOperator}`;
  //               break;

  //             case 11: // starts with
  //               condition += `LOWER(${fieldName}) LIKE LOWER('${stringFieldValue}%')`;
  //               break;

  //             case 12: // ends with
  //               condition += `LOWER(${fieldName}) LIKE LOWER('%${stringFieldValue}')`;
  //               break;

  //             case 13: // contains
  //               fieldName.toLowerCase().includes("arabic") ? condition += `${fieldName} LIKE N'%${stringFieldValue}%'` :
  //                 condition += `LOWER(${fieldName}) LIKE LOWER('%${stringFieldValue}%')`;
  //               //  condition += `LOWER(${fieldName}) LIKE LOWER('%${stringFieldValue}%')`;
  //               break;

  //             case 14: // not contains
  //               // condition += `LOWER(${fieldName}) NOT LIKE LOWER('%${stringFieldValue}%')`;
  //               condition += `LOWER(TRIM(${fieldName})) LIKE LOWER('%${stringFieldValue}%')`;

  //               break;

  //             default: // numerical and other operators
  //               condition += `${fieldName} ${fieldOperatorValue} ${fieldValue}`;
  //               break;
  //           }

  //           if (condition !== "") {
  //             if (where !== "") {
  //               where = where.trim();
  //               if (!where.endsWith("and")) {
  //                 where += " and";
  //               }
  //               where += ` ${condition}`;
  //             } else {
  //               where += condition;
  //             }
  //           }

  //         }
  //       }
  //     });
  //   }

  //   return where || "1=1";
  // };


  const getQueryOptions = () => {
    const where = getQueryWhere();
    if (where) {
      let queryOptions = {};
      queryOptions.where = where;
      queryOptions.returnGeometry = true;
      queryOptions.outFields = ["*"];
      return queryOptions;
    }
  };

  const validateDates = (filter) => {
    const newErrors = {};
    const currentDate = new Date();

    //console.log(filter.startDate, filter.endDate)
    if (filter.startDate && !filter.endDate) {
      if (new Date(filter.startDate) > currentDate) {
        newErrors.startDate = t("startDateCannotBeInTheFuture");
      }
    } else if (!filter.startDate && filter.endDate) {
      newErrors.startDate = t("pleaseSelectStartDate");
    } else if (filter.startDate && filter.endDate) {
      if (new Date(filter.startDate) > new Date(filter.endDate)) {
        newErrors.startDate = t("startDateCannotBeAfterEndDate");
      }
    }
    setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateFieldInput = (fieldObj, fieldValue) => {
    const newErrors = {};
    const fieldType = fieldObj.mapFieldType;

    if (fieldType === "esriFieldTypeString") {
      if (typeof fieldValue !== "string" || fieldValue.trim() === "") {
        newErrors[fieldObj.mapFieldName] = t("invalidInput");
      }
    } else if (fieldType === "esriFieldTypeSmallInteger") {
      if (!Number.isInteger(Number(fieldValue))) {
        newErrors[fieldObj.mapFieldName] = t("invalidInput");
      }
    } else if (fieldType === "esriFieldTypeInteger") {
      if (!Number.isInteger(Number(fieldValue))) {
        newErrors[fieldObj.mapFieldName] = t("invalidInput");
      }
    }
    else if (fieldType === "esriFieldTypeDouble") {
      if (isNaN(Number(fieldValue))) {
        newErrors[fieldObj.mapFieldName] = t("invalidInput");
      }
    }
    if (Object.values(newErrors).every(value => value !== "")) {
      // console.log('field validation errors', newErrors);
    }
    setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
    return Object.keys(newErrors).length === 0;
    // return {
    //   isValid: Object.keys(newErrors).length === 0,
    //   errors: newErrors,
    // };
  };

  const options = [
    { value: "", label: "Select Map Service" }, // Default option
    ...data.map((item) => ({
      value: item.id,
      label: item[`${i18n.language}MapServiceName`],
    })),
  ];

  const Layeroptions = [
    { value: "", label: "Select Layer" }, // Default option
    ...(queryLayers || []).map((item) => ({
      value: item.id,
      label: item[`queryName_${capitalizeFirstLetter(i18n.language)}`],
    })),
  ];

  return (
    <div className="data-search mt-3">
      <div className="form-group">
        <span className="text-danger">*</span>{" "}
        <label className="mb-1">{t("mapService")}</label>

        <Select
          components={{
            DropdownIndicator: CustomDropdownIndicator,
          }}
          className={`react-select-container`}
          classNamePrefix="react-select"
          options={options}
          value={options ? options.find(option => option.value === service) : null}
          onChange={(selectedOption) => handleMapServiceChange(selectedOption.value, selectedOption.label)}  // Handle value change

        />

      </div>
      <div className="form-group">
        <span className="text-danger">*</span>{" "}
        <label className="mb-1">{t("searchQuery")}</label>

        <Select
          components={{
            DropdownIndicator: CustomDropdownIndicator,
          }}
          className={`react-select-container`}
          classNamePrefix="react-select"
          options={Layeroptions}
          value={Layeroptions ? Layeroptions.find(option => option.value === queryLayer) : null}
          onChange={(selectedOption) => handleLayerChange(selectedOption.value, selectedOption.label)}

        />
        {errors && errors.queryLayer && (
          <div className="text-danger mt-1">{errors.queryLayer}</div>
        )}
      </div>
      {showFilters && layerFilter && layerFilter.length > 0 && (
        <div className="filter-criterias dataFilterScroll mt-3 m-1 p-2">
          {layerFilter && layerFilter.map((item) => (
            <div className="criteria" key={item.mapFieldName}>
              <div
                className="field-label"
                title={item[`hint_${capitalizeFirstLetter(i18n.language)}`]}
              >
                {item[`prompt${capitalizeFirstLetter(i18n.language)}`]}
              </div>
              <div className="filter-wrapper d-flex">
                <div className="operator-label">
                  {item.operatorSymbol === "All" ? (
                    <select
                      className="operator w-100 text-green form-select"
                      value={fieldValuesState[item.mapFieldName]?.operator}
                      onChange={(event) =>
                        handleOperatorChange(item.mapFieldName, event)
                      }
                    >
                      <option value="">{t("select")}</option>

                      {window.dataSearchNumOperators &&
                        window.dataSearchNumOperators.length > 0 &&
                        window.dataSearchNumOperators.map((opitem, index) => (
                          <option
                            key={`${item.mapFieldName}|${opitem}`}
                            value={opitem}
                          >
                            {opitem}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <span className="operator text-green">
                      {item.operatorSymbol}
                    </span>
                  )}
                </div>
                {(() => {
                  switch (item.controlsNo) {
                    case 1:
                      return item.isUniqueEnable ? (
                        fieldValues &&
                        Object.keys(fieldValues).length > 0 &&
                        fieldValuesState &&
                        Object.values(fieldValuesState).length > 0 && (
                          <>
                            {/* {console.log(fieldValuesState[item.mapFieldName]?.filteredData)} */}
                            {/* <Select
                              key={item.mapFieldName}
                              placeholder={t("selectValue")}
                              options={(fieldValuesState[item.mapFieldName]?.filteredData || []).filter(item => item?.value !== undefined
                                && item?.value !== null).map(item => ({ label: String(item.value), value: String(item.value) }))}
                              // options={(fieldValuesState[item.mapFieldName]?.filteredData)
                              //   .filter(item => item.value !== undefined && item.value !== null) // Remove undefined/null values
                              //   .map(item => ({
                              //     label: String(item.value), // Ensure label is a string
                              //     value: String(item.value)  // Ensure value is a string
                              //   }))|| []
                              // }
                              // styles={getCustomStyles(theme)}
                              className={`react-select-container field-value w-100`}
                              classNamePrefix="react-select"
                              onChange={(selectedOption) =>
                                handleChange(item.mapFieldName, selectedOption)
                              }
                              components={{
                                DropdownIndicator: CustomDropdownIndicator, // Add the custom dropdown indicator
                              }}
                              isSearchable={true}
                              noOptionsMessage={() => "No data available"}
                            /> */}
                            <Select
                              isMulti
                              placeholder={t("selectValue")}
                              options={(fieldValuesState[item.mapFieldName]?.filteredData || [])
                                .filter(item => item?.value !== undefined && item?.value !== null)
                                .map(item => ({ label: String(item.value), value: String(item.value) }))
                              }
                              className={`react-select-container field-value w-100`}
                              classNamePrefix="react-select"
                              onChange={(selectedOptions) =>
                                handleChange(item.mapFieldName, selectedOptions)
                              }
                              components={{ DropdownIndicator: CustomDropdownIndicator }}
                              isSearchable={true}
                              noOptionsMessage={() => "No data available"}
                            />


                          </>
                        )
                      ) : (
                        <input
                          type="text"
                          className="field-value form-control"
                          value={fieldValuesState[item.mapFieldName]?.value}
                          onChange={(event) =>
                            handleInputChange(item.mapFieldName, event)
                          }
                        />
                      );
                    case 2:
                      return (
                        <div className="d-flex flex-column">
                          <div className="date">
                            <input
                              type="date"
                              name="startDate"
                              value={
                                //fieldValuesState[item.mapFieldName].startDate
                                fieldValuesState[item.mapFieldName]?.startDate || ""
                              }
                              onChange={(event) =>
                                handleDateChange(
                                  item.mapFieldName,
                                  event,
                                  "startDate"
                                )
                              }
                              placeholder={t("Start Date")}
                              className="form-control"
                            />
                            {errors.startDate && (
                              <div className="text-danger">
                                {errors.startDate}
                              </div>
                            )}
                          </div>
                          <div className="date">
                            <input
                              type="date"
                              name="endDate"
                              value={
                                //fieldValuesState[item.mapFieldName].endDate
                                fieldValuesState[item.mapFieldName]?.endDate || ""
                              }
                              onChange={(event) =>
                                handleDateChange(
                                  item.mapFieldName,
                                  event,
                                  "endDate"
                                )
                              }
                              placeholder={t("End Date")}
                              className="form-control"
                            />
                            {errors.endDate && (
                              <div className="text-danger">
                                {errors.endDate}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    case null:
                      return (
                        <input
                          type="text"
                          className="field-value form-control"
                          value={fieldValuesState[item.mapFieldName]?.value}
                          onChange={(event) =>
                            handleInputChange(item.mapFieldName, event)
                          }
                        />
                      );
                    case 0:
                    default:
                      return null;
                  }
                })()}
              </div>
              {errors[item.mapFieldName] && <span className="text-danger">{errors[item.mapFieldName]}</span>}
            </div>
          ))}
        </div>
      )}
      {queryLayer && errors && errors.queryFilter && (
        <div className="text-danger mt-1">{errors.queryFilter}</div>
      )}
    </div>
  );
});

export default DataSearch;
